import { getMadState, useMadState } from "../state";
/**
 * Hook for setting the context sent with every prompt to an agent chat session, from anywhere in the app.
 * Context is shared across hook instances with the same `agent` and `contextKey`,
 * and `useAgentChat` merges it over its own `context` option each time a message is sent.
 * The shared context is cleared once no instance of this hook is mounted for the key.
 * @param agent The name of the AI agent. {@link AgentName}
 * @param contextKey The session ID given to the chat (e.g., "chat:general"), or its `contextKey` option when set.
 * @returns The current context and a setter that accepts a value or an updater, like `useState`. The setter keeps its identity until `contextKey` changes.
 */
export function useAgentChatContext(agent, contextKey) {
    return useMadState(makeContextKey(agent, contextKey));
}
/**
 * Reads the context shared through `useAgentChatContext` without subscribing to it,
 * so the caller neither re-renders on changes nor keeps the context alive.
 * @param agent The name of the AI agent.
 * @param contextKey The chat's context key.
 * @returns The shared context without attachments, or undefined once no `useAgentChatContext` instance is mounted for the key.
 */
export function getAgentChatContext(agent, contextKey) {
    const context = getMadState(makeContextKey(agent, contextKey));
    if (!context) {
        return undefined;
    }
    // The type already excludes attachments, but a cast can still slip them in.
    const { attachments: _attachments, ...sharedContext } = context;
    return sharedContext;
}
/**
 * Generates the shared state key for an agent chat's context
 * @param agent The name of the AI agent.
 * @param contextKey The chat's context key.
 * @returns {string} shared state key
 */
function makeContextKey(agent, contextKey) {
    return `${agent}:${contextKey}:context`;
}
//# sourceMappingURL=chat.context.js.map